// In dev, /api goes to the Worker via the Vite proxy. In prod, the app sits at
// nanorhino.com/tdee/, so we prefix /tdee/api/... matching how chat does it.
const API_BASE = import.meta.env.DEV ? '' : import.meta.env.BASE_URL.replace(/\/$/, '')

export interface DailyPoint {
  date: string
  sessions: number
  messages: number
  calcs: number
}
// Daily sessions split by acquisition channel. `channels` is sparse — only
// channels with sessions that day appear — and the keys are SourceChannel
// values from the server's shared deriveChannel (google_ads, openai, x,
// reddit, google_organic, other, direct). Typed as a loose record so a new
// server-side channel renders (in the Other bucket) instead of type-erroring.
export interface DailySourcePoint {
  date: string
  channels: Record<string, number>
}
export interface WeeklyPoint {
  week_start: string
  sessions: number
  messages: number
  calcs: number
}
export interface CountryRow {
  country: string
  sessions: number
  calcs: number
}
export interface CountRow<K extends string = string> {
  [k: string]: string | number | null
  n: number
}

export interface StatsResponse {
  generated_at: string
  excluded: boolean
  excluded_ids: string[]
  totals: {
    conversations: number
    messages: number
    calcs: number
    engaged_4plus: number
    reached_calc: number
    recalced: number
    avg_msgs: number | null
    handoff_tokens_minted: number
    handoff_cta_shown: number
    handoff_sms_clicked: number
    // WhatsApp CTA taps (2026-07-22 WhatsApp-primary switch). handoff_wa_clicked
    // is global (intl gets the WhatsApp CTA too); wa_cta_clicked_us is US-only;
    // us_cta_clicked_any = distinct US convs that tapped EITHER the WhatsApp or
    // SMS button, so the US conversion survives the SMS→WhatsApp channel shift.
    handoff_wa_clicked: number
    wa_cta_clicked_us: number
    us_cta_clicked_any: number
    // Closed loop: EC2 coach fetched the profile on first inbound SMS.
    handoff_redeemed: number
    handoff_emails_captured: number
    chat_emails_captured: number
    emails_any_source: number
    feedback_count: number
    feedback_avg: number | null
    channel_picked: number
    // US SMS-CTA funnel (2026-06-10 redesign): CTA shown right after the
    // calc card / SMS link tapped. Both restricted to country='US'.
    sms_cta_shown: number
    sms_cta_clicked: number
    // Redeemed = coach picked the user up (server-to-server profile fetch on
    // the first inbound SMS). The true tap -> coach conversion; US-scoped.
    sms_cta_redeemed: number
    // INTL-ONLY since 2026-06-10: plans.kind='plan' rows only exist for the
    // intl flow (plan offer → in-chat plan → email CTA).
    plan_cta_shown: number
    plan_cta_converted: number
  }
  daily: DailyPoint[]
  // Absent on a worker deployed before 2026-07-31 — callers must tolerate
  // undefined (the dashboard falls back to the un-split session bars).
  daily_sources?: DailySourcePoint[]
  weekly: WeeklyPoint[]
  countries: CountryRow[]
  demographics: {
    sex: CountRow[]
    age_buckets: CountRow[]
    occupation: CountRow[]
  }
}

export async function fetchStats(token: string, exclude: boolean): Promise<StatsResponse> {
  const res = await fetch(`${API_BASE}/api/admin/stats?exclude=${exclude ? 'true' : 'false'}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.status === 401) throw new Error('unauthorized')
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`stats failed: ${res.status} ${text}`)
  }
  return res.json()
}

export interface SessionSummary {
  id: string
  created_at: number
  updated_at: number
  country: string | null
  strategy: string
  message_count: number
  calc_count: number
  first_user_msg: string | null
  // Funnel flags surfaced as SQLite 0/1 ints. 1 = a plan markdown row
  // exists for this conversation in the plans table. cta_clicked is 1
  // when the WhatsApp link (2026-07-22 primary US CTA), the SMS link, OR
  // the non-US email form was submitted on the post-plan CTA card.
  has_plan: 0 | 1
  cta_clicked: 0 | 1
  // Per-channel breakdown of cta_clicked so the list can show WHICH surface
  // converted (WhatsApp is now the dominant US path). A session can set more
  // than one (e.g. tapped WhatsApp then SMS).
  cta_wa: 0 | 1
  cta_sms: 0 | 1
  cta_email: 0 | 1
  // 1 = the user reported a GLP-1 medication in any calc input for this
  // session (medications array contains 'glp1'). A user segment, not a funnel
  // step — rendered as a distinct pill rather than a ✓ funnel flag.
  is_glp1: 0 | 1
  // Acquisition channel bucket, derived server-side (deriveChannel in
  // routes/handoff.ts) from first-touch attribution — same rules as the
  // handoff conversation endpoint. Raw fields ride along for the tooltip.
  source_channel: 'google_ads' | 'google_organic' | 'reddit' | 'openai' | 'x' | 'other' | 'direct'
  utm_source: string | null
  utm_campaign: string | null
  utm_term: string | null
  referrer: string | null
}

export interface SessionListResponse {
  total: number
  limit: number
  offset: number
  sessions: SessionSummary[]
}

export async function fetchSessions(
  token: string,
  opts: { limit?: number; offset?: number; exclude?: boolean } = {}
): Promise<SessionListResponse> {
  const params = new URLSearchParams({
    limit: String(opts.limit ?? 50),
    offset: String(opts.offset ?? 0),
    exclude: opts.exclude === false ? 'false' : 'true',
  })
  const res = await fetch(`${API_BASE}/api/admin/sessions?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.status === 401) throw new Error('unauthorized')
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`sessions failed: ${res.status} ${text}`)
  }
  return res.json()
}

export interface ConversationRow {
  id: string
  created_at: number
  updated_at: number
  strategy: string
  message_count: number
  calc_count: number
  last_calc_id: number | null
  user_agent: string | null
  country: string | null
  // Behavior / funnel fields. The detail endpoint returns SELECT * on
  // conversations, so these have always been in the payload — they were
  // just untyped before the behavior-timeline panel needed them.
  handoff_token: string | null
  cta_shown_at: number | null
  sms_link_clicked_at: number | null
  // WhatsApp CTA tap (2026-07-22 WhatsApp-primary switch). Null until the user
  // taps the WhatsApp deep link; the detail endpoint SELECTs * so it's always
  // in the payload.
  wa_link_clicked_at: number | null
  email_captured: string | null
  email_capture_source: string | null // 'us_fallback' | 'intl_form' | null
  initial_email_sent_at: number | null
  unsubscribed_at: number | null
  share_view_count: number
  share_copied_at: number | null
  language: string | null
  timezone: string | null
  utm_source: string | null
}

export interface MessageRow {
  id: number
  role: 'user' | 'assistant'
  content: string
  turn_index: number
  created_at: number
}

export interface TdeeResultRow {
  id: number
  created_at: number
  sex: string
  age_years: number
  height_cm: number
  weight_kg: number
  body_fat_pct: number | null
  occupation: string
  diet_style: string
  exercise_count: number
  bmr_kcal: number
  bmr_method: string
  neat_low: number
  neat_high: number
  tef_low: number
  tef_high: number
  eat_low: number
  eat_high: number
  tdee_low: number
  tdee_high: number
  tdee_recommended: number
  recommendation_strategy: string
  traditional_tdee: number
  traditional_bucket: string
  input_json: string
  output_json: string
}

export interface EmailThreadEntry {
  direction: 'sent' | 'received'
  at: number
  subject: string | null
  from: string
  to: string
  body: string
  kind?: 'initial_plan' | 'reply'
  error?: string | null
}

export interface PlanRow {
  kind: 'plan' | 'meal'
  markdown_body: string
  model_provider: string | null
  generated_at: number
}

export interface SessionDetailResponse {
  conversation: ConversationRow
  messages: MessageRow[]
  results: TdeeResultRow[]
  plans: PlanRow[]
  emails: EmailThreadEntry[]
}

export async function fetchSession(
  token: string,
  id: string
): Promise<SessionDetailResponse> {
  const res = await fetch(`${API_BASE}/api/admin/sessions/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.status === 401) throw new Error('unauthorized')
  if (res.status === 404) throw new Error('not_found')
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`session failed: ${res.status} ${text}`)
  }
  return res.json()
}

// ---- Email outbox ----------------------------------------------------------

export interface EmailSentRow {
  id: number
  conversation_id: string
  to_addr: string
  from_addr: string
  subject: string
  kind: 'initial_plan' | 'reply'
  provider_msg_id: string | null
  sent_at: number
  error: string | null
}

export interface EmailReceivedRow {
  id: number
  conversation_id: string
  from_addr: string
  to_addr: string
  subject: string | null
  body_preview: string
  received_at: number
}

export interface EmailLogResponse {
  sent: EmailSentRow[]
  received: EmailReceivedRow[]
}

export async function fetchEmailLog(token: string, limit = 50): Promise<EmailLogResponse> {
  const res = await fetch(`${API_BASE}/api/admin/emails?limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.status === 401) throw new Error('unauthorized')
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`email log failed: ${res.status} ${text}`)
  }
  return res.json()
}

export interface BatchRowResult {
  conversation_id: string
  status: 'sent' | 'dry_run' | 'skipped' | 'error' | 'queued'
  to?: string
  subject?: string
  provider_msg_id?: string
  plan_was_freshly_generated?: boolean
  error?: string
}

export interface SendBatchResponse {
  requested: number
  candidates: number
  dry_run: boolean
  test_to: string | null
  mode?: 'sync' | 'async'
  claimed?: number
  per_invocation_max?: number
  remaining_after?: number
  note?: string
  results: BatchRowResult[]
}

export interface SendBatchArgs {
  batch_size?: number
  dry_run?: boolean
  test_to?: string
}

export async function sendEmailBatch(
  token: string,
  args: SendBatchArgs
): Promise<SendBatchResponse> {
  const res = await fetch(`${API_BASE}/api/admin/emails/send-batch`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(args),
  })
  if (res.status === 401) throw new Error('unauthorized')
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`send-batch failed: ${res.status} ${text}`)
  }
  return res.json()
}

export interface EmailPreviewResponse {
  conversation_id: string
  plan_was_freshly_generated: boolean
  to: string
  from: string | null
  reply_to: string
  subject: string
  plan_markdown: string
  html: string
  text: string
}

export async function fetchEmailPreview(
  token: string,
  id: string
): Promise<EmailPreviewResponse> {
  const res = await fetch(`${API_BASE}/api/admin/emails/preview/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.status === 401) throw new Error('unauthorized')
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`preview failed: ${res.status} ${text}`)
  }
  return res.json()
}

// --- Abuse block list --------------------------------------------------------

export interface BlockedIpRow {
  ip: string
  blocked_at: number
  last_seen_at: number
  hits: number
  reason: string
  sample: string | null
  conversation_id: string | null
  country: string | null
  unblocked_at: number | null
}

export async function fetchBlockedIps(token: string, limit = 100): Promise<BlockedIpRow[]> {
  const res = await fetch(`${API_BASE}/api/admin/blocked?limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.status === 401) throw new Error('unauthorized')
  if (!res.ok) throw new Error(`blocked list failed: ${res.status}`)
  const body = (await res.json()) as { blocked?: BlockedIpRow[] }
  return body.blocked ?? []
}

export async function setIpBlocked(
  token: string,
  ip: string,
  blocked: boolean
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/blocked/${encodeURIComponent(ip)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ blocked }),
  })
  if (res.status === 401) throw new Error('unauthorized')
  if (!res.ok) throw new Error(`block update failed: ${res.status}`)
}
